import UserService from "./UserService";
import Kichiri from "./KichiriService";
import moment from "moment";
import UtilityService from "./UtilityService";


const ClientService = {
	/**
	 * Returns a client's information
	 * @param {Number} clientId
	 * @return {Object}
	 */
	async getClient({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.client.get(
				{
					clientId
				},
				{},
				userData.authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Updates a client's information
	 * @param {Number} teamId
	 * @param {Number} seniorManagerId
	 * @param {Number} clientId
	 * @param {Number} partnerId
	 * @param {Number} managerId
	 * @param {String} name
	 * @param {Date} yearEnd
	 * @param {String} address
	 * @param {String} notes
	 * @param {String} craBusinessNumber
	 * @param {Boolean} isHst
	 * @param {Boolean} isPayroll
	 * @param {Boolean} isEht
	 * @param {Boolean} isWsib
	 * @param {String} faxNumber
	 * @param {String} status
	 * @param {String} customFields
	 * @param {String} email
	 * @param {String} description
	 * @return {Object}
	 */
	async updateClient({
		teamId,
		clientGroupId,
		seniorManagerId,
		bookkeeperManagerId,
		clientId,
		partnerId,
		managerId,
		name,
		yearEnd,
		address,
		notes,
		craBusinessNumber,
		isHst,
		isPayroll,
		isEht,
		isWsib,
		faxNumber,
		operatingName,
		status,
		customFields,
		email,
		description,
		hstNumber
	}) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.client.update(
				{
					teamId,
					clientGroupId,
					seniorManagerId,
					bookkeeperManagerId,
					clientId,
					partnerId,
					managerId,
					name,
					yearEnd: UtilityService.toDateOnly({value: yearEnd}),
					address,
					notes,
					craBusinessNumber,
					isHst,
					isPayroll,
					isEht,
					isWsib,
					faxNumber,
					operatingName,
					status,
					customFields,
					email,
					description,
					hstNumber
				},
				{},
				userData.authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Creates a client
	 * @param {String} type
	 * @param {Number} partnerId
	 * @param {Number} managerId
	 * @param {String} name
	 * @param {Date} yearEnd
	 * @param {String} address
	 * @param {String} notes
	 * @param {String} craBusinessNumber
	 * @param {Boolean} isHst
	 * @param {Boolean} isPayroll
	 * @param {Boolean} isEht
	 * @param {Boolean} isWsib
	 * @param {String} faxNumber
	 * @param {String} operatingName
	 * @param {Number} teamId
	 * @param {Number} seniorManagerId
	 * @param {String} customFields
	 * @param {String} email
	 * @param {String} description
	 * @return {Object}
	 */
	async createClient({
		type,
		partnerId,
		managerId,
		name,
		yearEnd,
		address,
		notes,
		craBusinessNumber,
		isHst,
		isPayroll,
		isEht,
		isWsib,
		faxNumber,
		operatingName,
		teamId,
		seniorManagerId,
		bookkeeperManagerId,
		customFields,
		email,
		status = "active",
		description,
		hstNumber
	}) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.client.create(
				{
					companyId: userData.companyId,
					type,
					partnerId,
					managerId,
					name,
					yearEnd: UtilityService.toDateOnly({value: yearEnd}),
					address,
					notes,
					craBusinessNumber,
					isHst,
					isPayroll,
					isEht,
					isWsib,
					faxNumber,
					operatingName,
					teamId,
					seniorManagerId,
					bookkeeperManagerId,
					status,
					customFields,
					email,
					description,
					hstNumber
				},
				{},
				userData.authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Returns the sin number in displayed format
	 * @param {String} sin
	 * @return {String}
	 */
	concatSin({ sin }) {
		let concat = "";

		// replace a dash with an empty string
		sin = sin.replace(/-/g, "");

		// Loop through the string
		for (var i = 0; i < sin.length; i++) {
			concat += sin[i];

			// if the 3rd or 6th index of the string has been reached
			if (i === 2 || i === 5) {
				// add a dash
				concat += "-";
			}
		}

		return concat;
	},

	/**
	 * Returns the sin number in displayed subtracted format
	 * @param sin
	 * @return {String}
	 */
	concatSinMinus({ sin }) {
		// remove the last element in the string
		sin = sin.substring(0, sin.length - 1);
		return sin;
	},

	/**
	 * Deletes a client
	 * @param {Number} clientId
	 * @return {Boolean}
	 */
	async deleteClient({ clientId }) {
		let { authToken } = UserService.getUserData();

		try {
			await Kichiri.client.delete({ clientId }, {}, authToken);

			return true;
		} catch (error) {
			console.log(error);
		}

		return false;
	},

	/**
	 * Fetches the entire client summary
	 *
	 * @param {Number} limit
	 * @param {Number} offset
	 * @param {Number} partnerId
	 * @param {Number} assignedUserId
	 * @param {Number} bookkeeperId
	 * @param {String} type
	 * @param {String} search
	 * @returns {Object}
	 */
	async fetchClientSummary({ limit = 50, offset = 0, partnerId = 0, assignedUserId = 0, bookkeeperId = 0, type, search = "" }) {
		let user = UserService.getUserData();

		try {
			let response = await Kichiri.client.summary(
				{},
				{
					limit,
					offset,
					partnerId,
					assignedUserId,
					bookkeeperId,
					type,
					search
				},
				user.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Fetches a clients info
	 * @param {Number} clientId
	 * @returns {Object}
	 */
	async fetchClient({ clientId }) {
		let user = UserService.getUserData();

		try {
			let response = await Kichiri.client.get(
				{
					clientId
				},
				{},
				user.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Fetches a client's custom field
	 * @param {Number} clientId
	 * @returns {Object}
	 */
	async fetchCustomFields({ clientId }) {
		let user = UserService.getUserData();

		try {
			let response = await Kichiri.client.getCustomFields(
				{
					clientId
				},
				{},
				user.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Fetches a list of clients given a search query
	 * @param {Object} request
	 * @returns {Array}
	 */
	async search({ request }) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.client.search(request, {}, userData.authToken);
			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Fetches a list of clients given a search query
	 * @param {Object} request
	 * @returns {Array}
	 */
	async searchClients({ search }) {
		let userData = UserService.getUserData();
		try {
			let response = await Kichiri.client.search(
				{
					searchTerm: search
				},
				{},
				userData.authToken
			);

			return response;
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Fetches a list of client groups
	 *
	 * @param {String} search
	 * @returns
	 */
	async fetchClientGroups({ search, clientId }) {
		try {
			let { authToken, companyId } = UserService.getUserData();

			let { data: clientGroups } = await Kichiri.client.listGroups({}, { companyId, searchTerm: search, clientId }, authToken);

			return clientGroups;
		} catch (error) {
			console.log(error);
		}

		return [];
	},

	/**
	 * Fetches a client group
	 *
	 * @param {Number} clientGroupId
	 * @returns
	 */
	async fetchClientGroup({ clientGroupId }) {
		try {
			let { authToken } = UserService.getUserData();

			let { data: clientGroup } = await Kichiri.client.getClientGroup({ clientGroupId }, {}, authToken);

			return clientGroup;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Updates a client group
	 *
	 * @param {Number} clientGroupId
	 * @param {String} clientGroupName
	 * @param {Array<Object>} assignedClients
	 * @param {String} status
	 * @returns
	 */
	async updateClientGroup({ clientGroupId, name, assignedClients, status }) {
		try {
			let { authToken } = UserService.getUserData();

			let { data: clientGroup } = await Kichiri.client.updateClientGroup({ clientGroupId, name, assignedClients, status }, {}, authToken);

			return clientGroup;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Creates a new client group
	 *
	 * @param {String} clientGroupName
	 * @param {Array<Object>} assignedClients
	 * @returns
	 */
	async createClientGroup({ name, assignedClients }) {
		try {
			let { authToken, companyId } = UserService.getUserData();

			let { data: clientGroup } = await Kichiri.client.createClientGroup({ name, assignedClients, companyId }, {}, authToken);

			return clientGroup;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Searches Google Drive for folders whose name contains the client's name.
	 * Returns an array of { id, name, url } for the user to choose from.
	 *
	 * @param {Number} clientId
	 * @returns {Array|null}
	 */
	async searchDriveFolders({ clientId, query, pageToken }) {
		let { authToken } = UserService.getUserData();
		const queryParams = {};
		if (query) queryParams.query = query;
		if (pageToken) queryParams.pageToken = pageToken;
		try {
			let response = await Kichiri.client.searchDriveFolder({ clientId }, queryParams, authToken);
			return response.data;
		} catch (error) {
			console.error("[ClientService.searchDriveFolders]", error);
		}
		return null;
	},

	/**
	 * Gets the active Google Drive folder mapping for a client.
	 * Returns null if no mapping exists yet (404).
	 *
	 * @param {Number} clientId
	 * @returns {Object|null}
	 */
	async getDriveFolder({ clientId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.client.getDriveFolder({ clientId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.error("[ClientService.getDriveFolder]", error);
		}
		return null;
	},

	/**
	 * Manually maps an existing Google Drive folder to a client.
	 *
	 * @param {Number} clientId
	 * @param {String} driveFolderId
	 * @param {String} driveFolderUrl
	 * @returns {Object|null}
	 */
	async mapDriveFolder({ clientId, driveFolderId, driveFolderUrl, driveFolderName }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.client.mapDriveFolder({ clientId, driveFolderId, driveFolderUrl, driveFolderName }, {}, authToken);
			return response.data;
		} catch (error) {
			console.error("[ClientService.mapDriveFolder]", error);
		}
		return null;
	},

	/**
	 * Renames the linked Google Drive folder to match the client's name in WF.
	 *
	 * @param {Number} clientId
	 * @returns {Object|null} Updated folder object on success, null on failure
	 */
	async renameDriveFolder({ clientId }) {
		let { authToken } = UserService.getUserData();
		try {
			let response = await Kichiri.client.renameDriveFolder({ clientId }, {}, authToken);
			return response.data;
		} catch (error) {
			console.error("[ClientService.renameDriveFolder]", error);
		}
		return null;
	},

	/**
	 * Removes the active Google Drive folder mapping for a client.
	 *
	 * @param {Number} clientId
	 * @returns {Boolean} true on success, false on failure
	 */
	async unlinkDriveFolder({ clientId }) {
		let { authToken } = UserService.getUserData();
		try {
			await Kichiri.client.unlinkDriveFolder({ clientId }, {}, authToken);
			return true;
		} catch (error) {
			console.error("[ClientService.unlinkDriveFolder]", error);
		}
		return false;
	}
};

export default ClientService;
